import { createRecipeSlug } from "base/utils";

const toIsoDuration = (minutes) => {
  if (!minutes && minutes !== 0) return undefined;
  const mins = parseInt(minutes, 10);
  if (isNaN(mins) || mins <= 0) return undefined;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  let duration = "PT";
  if (hours > 0) duration += `${hours}H`;
  if (remainingMins > 0) duration += `${remainingMins}M`;
  return duration;
};
const createRecipeUrl = (recipe, siteUrl) => {
  const slug = createRecipeSlug(recipe);
  return siteUrl && slug ? `${siteUrl}/recipes/${slug}` : undefined;
};

const recipeToJsonLd = (recipe, siteUrl) => {
  if (!recipe) return null;
  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Recipe",
    name: recipe.recipe_name,
  };

  // Images
  const images = [
    recipe.main_picture,
    ...(recipe.additional_pictures || []),
  ].filter(Boolean);
  if (images.length) {
    jsonLd.image = images;
  }

  // Yield / servings
  if (recipe.num_of_servings) {
    jsonLd.recipeYield = `${recipe.num_of_servings} servings`;
  }

  // Durations
  const prepTime = toIsoDuration(recipe.prep_time);
  const cookTime = toIsoDuration(recipe.cook_time);
  if (prepTime) {
    jsonLd.prepTime = prepTime;
  }

  if (cookTime) {
    jsonLd.cookTime = cookTime;
  }

  if (prepTime || cookTime) {
    const totalMins =
      (parseInt(recipe.prep_time, 10) || 0) +
      (parseInt(recipe.cook_time, 10) || 0);
    const totalTime = toIsoDuration(totalMins);
    if (totalTime) {
      jsonLd.totalTime = totalTime;
    }
  }

  // Ingredients
  if (recipe.ingredients?.length) {
    jsonLd.recipeIngredient = [...recipe.ingredients]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((ing) =>
        [ing.quantity, ing.measurement, ing.name].filter(Boolean).join(" "),
      );
  }

  // Steps
  if (recipe.steps?.length) {
    jsonLd.recipeInstructions = [...recipe.steps]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((step) => ({
        "@type": "HowToStep",
        text: step.detail,
      }));
  }

  // Nutrition
  if (recipe.calories) {
    jsonLd.nutrition = {
      "@type": "NutritionInformation",
      calories: `${recipe.calories} calories`,
    };
  }

  // SEO-friendly recipe URL
  const recipeUrl = createRecipeUrl(recipe, siteUrl);
  if (recipeUrl) {
    jsonLd.url = recipeUrl;
  }

  // Author
  jsonLd.author = {
    "@type": recipe.created_by ? "Person" : "Organization",
    name: recipe.created_by_name ?? "Blog Free Recipe",
  };

  // Keywords (aka badges)
  if (recipe.badges?.length) {
    jsonLd.keywords = recipe.badges
      .map((badge) => badge.name)
      .filter(Boolean)
      .join(", ");
  }
  // Published date
  if (recipe.created_at) {
    jsonLd.datePublished = recipe.created_at;
  }

  //Cuisine badge
  if (recipe.badges?.length && recipe.badges.some((b) => b.iscuisine)) {
    jsonLd.recipeCuisine = recipe.badges
      .flatMap((badge) => {
        return badge.iscuisine ? badge.name : [];
      })
      .filter(Boolean)
      .join(", ");
  }

  //Category badge
  if (recipe.badges?.length && recipe.badges.some((b) => b.iscategory)) {
    jsonLd.recipeCategory = recipe.badges
      .flatMap((badge) => {
        return badge.iscategory ? badge.name : [];
      })
      .filter(Boolean)
      .join(", ");
  }

  return jsonLd;
};

export default recipeToJsonLd;
